// HelpPage — 서비스 사용 안내(기능별 도움말).
import { useState } from 'react';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import HelpShotGallery from '../components/HelpShotGallery';
import useIsMobile from '../hooks/useIsMobile';
import {
  IconArchive,
  IconBell,
  IconBook,
  IconBook2,
  IconBuilding,
  IconBuildingWarehouse,
  IconCode,
  IconCoin,
  IconDatabase,
  IconDownload,
  IconFileInvoice,
  IconTrendingUp,
  IconCalendar,
  IconChartBar,
  IconClipboardList,
  IconFileText,
  IconFolder,
  IconHelpCircle,
  IconLayoutKanban,
  IconLock,
  IconMail,
  IconMessageCircle,
  IconNotebook,
  IconPhone,
  IconPresentation,
  IconPuzzle,
  IconSettings,
  IconSparkles,
  IconTicket,
  IconUsers,
  IconVideo,
} from '@tabler/icons-react';
import { COLORS, TONES, RADIUS, card as cardBase, input as inputBase } from '../utils/uiStyles';
import { Page, PageHeader } from '../components/ui';
import { SHOTS } from '../utils/helpShots';

// 기능 설명 / 사용 안내 허브. 최상위=실제 메뉴, children=그 메뉴 안의 하위 기능.
// 항목·내용은 이 배열만 수정하면 됨.
// 🔴 캡처(shots·phoneShots)는 아래 SHOTS_BY_KEY 에서 키로 붙인다 — 본문과 캡처 배치를
//    섞지 않아야 "어느 화면에 어느 캡처가 붙었나"를 한 표에서 전수로 볼 수 있다.
// 🔴 선택 필드 3개(steps·tips·faq)는 "그 화면에 실제로 보이는 글자"만 «» 로 적는다.
//    steps: [{ title, items }] — 번호 절차 / tips: [..] — 💡 상자 / faq: [{ q, a }] — 접이식.
//    화면 파일 좌표(파일:줄)는 설계서에만 두고 여기에는 넣지 않는다(사용자에게 보이는 글이다).
const HELP_DEFS = [
  { key: 'start', title: '👋 시작하기', lines: [
    '처음이신가요? 왼쪽 메뉴에서 기능을 고르면 됩니다. «메인» 화면에는 사용량·할 일·알림·일정 같은 위젯이 모여 있고, «⚙ 위젯 설정»으로 보일 위젯을 고를 수 있어요.',
    '주력 기능은 💬 AI 채팅 · 📝 AI 노트 · 📹 화상회의 세 가지예요. 나머지 기능도 함께 열려 있으니 자유롭게 써 보세요.',
    '메뉴는 권한에 따라 다르게 보여요. 필요한 기능이 안 보이면 문의게시판으로 관리자에게 요청하세요.',
    '왼쪽 사이드바 폭은 경계선을 끌어 조절하고, 이번 달 사용량은 오른쪽 위 동그란 내 계정 단추를 누르면 나오는 창에서 볼 수 있어요.',
    '왼쪽 사이드바 메뉴의 «내 정보»에서 알림 켜기·화면 폰트·비밀번호 변경 같은 개인 설정을 할 수 있어요.',
    'Ctrl+K(맥은 ⌘+K)를 누르거나 사이드바 맨 위의 돋보기 «검색»을 누르면 통합 검색 창이 열려요 — 대화·문서·회의록·게시판·문의를 한 번에 찾습니다. 메신저·드라이브·메일처럼 화면 안쪽을 누른 상태에서는 단축키가 안 먹을 수 있으니 그때는 «검색»을 누르세요.',
    '권한이 없는 메뉴 가운데 일부는 사이드바에 «(메뉴 이름) 권한 요청»으로 보여요. 누르면 문의게시판으로 가니, 거기에 필요한 메뉴를 적어 주세요.',
    '창이 넓으면(폭 1100px 이상) 사이드바의 일부 메뉴에 마우스를 올렸을 때 오른쪽에 나오는 아이콘(«보조 패널로 열기»)으로 두 화면을 나란히 볼 수 있어요. 닫을 때는 보조 패널 위쪽의 × 를 누릅니다(사이드바를 접었을 때는 이 아이콘이 나오지 않아요).',
  ], tips: [
    '«내 정보»의 «화면 폰트»에서 글꼴을 바꿀 수 있어요.',
  ] },
  { key: 'chat', title: '💬 AI 채팅', lines: [
    '궁금한 걸 물어보면 사내 Claude가 답해줘요 — 보고서 초안, 번역, 요약, 아이디어 정리 등 무엇이든요.',
    '파일도 같이 보낼 수 있어요: 📎 버튼·붙여넣기·끌어다 놓기 (이미지 5MB·문서 20MB·최대 20개 · 드라이브 폴더는 «드라이브 첨부»에서 통째로 첨부).',
    '최신·외부 정보가 필요하면 "웹 검색"을 켜두세요. 필요할 때만 AI가 알아서 검색합니다.',
    '답변은 «복사»와 «저장 ▾»(TXT·MD·Word·PDF)이 되고, 마음에 안 들면 «재생성», 멈추려면 "중지"를 누르세요.',
    '상황에 맞게 모델을 고를 수 있어요 — 빠른 Haiku · 기본 Sonnet · 고성능 Opus (권한 범위 내).',
    '대화는 ★즐겨찾기·🏷태그·이름변경·검색으로 정리돼요. 일반 대화는 최대 50개까지 보관됩니다.',
    '🔒 내 대화는 나만 봅니다. 프로젝트를 팀원과 공유해도 대화는 공유되지 않아요 — 공유되는 건 지침·지식문서·작업공간 파일이고, 대화 목록·본문·검색은 언제나 본인 것만 나옵니다.',
    '아래 보관함·프로젝트·코드 실행·사내데이터·문서 만들기는 모두 AI 채팅 안에서 쓰는 기능이에요(코드 실행은 프로젝트+작업공간이 필요해요).',
    '내가 보낸 메시지에 마우스를 올리면(휴대폰에서는 항상) «편집»·«삭제»가 보여요. 답변이 나오는 중에는 누를 수 없어요.',
    '대화 목록 위쪽의 «Artifacts»를 누르면 이 대화에서 AI가 쓴 답변만 모아 보고, 답변마다 «복사»하거나 TXT·MD·DOCX·PDF 파일로 저장할 수 있어요.',
  ], steps: [
    { title: '처음 질문하기', items: [
      '① 왼쪽 «새 대화»를 누릅니다.',
      '② 아래 입력칸(«무엇이든 물어보세요»)에 질문을 적습니다.',
      '③ 입력칸 오른쪽 ↑ 버튼을 누르거나 Enter.',
      '④ 답이 길어 멈추고 싶으면 같은 자리의 ■ 버튼을 누릅니다.',
    ] },
    { title: '파일과 함께 묻기', items: [
      '① 입력칸 옆 📎 버튼(최대 20개 · 이미지 5MB · 문서 20MB · 드라이브 폴더는 «드라이브 첨부»에서 통째로 첨부)',
      '② 파일을 고르거나 입력칸에 끌어다 놓기·붙여넣기',
      '③ 질문을 적어 보냅니다. 이미지 칩을 누르면 원본 크기로 봅니다.',
    ] },
    { title: '도구 켜기', items: [
      '① 입력칸 아래 막대에서 «웹 검색»·«심층» 버튼을 바로 누를 수 있습니다.',
      '② 나머지 도구는 막대의 «+» 버튼을 눌러 «도구» 목록에서 켭니다 — «사내데이터»(권한·사내망 필요) · «문서 기반 답변»(승인된 학습자료를 근거로 답하고 출처 표시) · «드라이브 첨부»(드라이브 권한 필요) · «프롬프트»(저장된 업무 프롬프트 삽입).',
      '③ 창이 아주 좁으면 «웹 검색»·«심층»도 «+» 목록 안으로 들어갑니다.',
      '뜻: «웹 검색» = 인터넷을 찾아 답함(검색 1건마다 요금이 더해짐) · «심층» = 더 오래 생각한 뒤 답함(지원 안 하는 모델도 있음).',
    ] },
    { title: '자주 쓰는 질문 저장하기', items: [
      '① «+» → «프롬프트» → «열기»',
      '② «프롬프트 라이브러리»에서 «＋ 현재 입력 저장»',
      '③ 다음부터 목록에서 골라 넣습니다. 항목 옆에 «내 프롬프트»·«공유» 표시가 붙어 있어요.',
    ] },
    { title: '답변 활용하기', items: [
      '답변 아래 «복사» · «저장 ▾»의 «TXT (텍스트) / MD (마크다운) / DOCX (워드) / PDF» · 마지막 답변에는 «재생성» · 출처가 있으면 «웹 / 프로젝트 문서 / 사내 문서» 출처가 함께 보입니다. 대화 전체는 화면 위쪽 «TXT»·«PDF» 버튼으로 내려받습니다.',
    ] },
    { title: '대화 정리하기', items: [
      '① PC 에서는 대화 줄에 마우스를 올리면 아이콘들이 나타납니다(휴대폰에서는 늘 보여요 · ★ 는 PC 에서도 늘 보여요).',
      '② ★ 아이콘 = 즐겨찾기 · ✏ 아이콘 = 제목 바꾸기(제목을 두 번 눌러도 됨) · 🏷 아이콘 = 태그 · 📥 아이콘 = 보관함에 보관 · × 아이콘 = 휴지통으로.',
      '③ 목록 위 «전체 / 즐겨찾기 / 보관함 / 휴지통» 칩으로 걸러 보고, «대화 검색...»으로 찾습니다.',
    ] },
  ], tips: [
    '«AI 응답은 부정확할 수 있습니다. 중요한 내용은 반드시 확인하세요.»',
    '목록 위 숫자 N/50 은 보관 중인 대화 수 / 최대 수예요(보관함은 빼고 셈).',
    '웹 검색은 켜 두어도 필요할 때만 검색해요.',
  ], faq: [
    { q: '지운 대화를 되살릴 수 있나요?', a: '목록 위 «휴지통» 칩에서 대화를 찾아 되살리기 아이콘(↺)을 누르세요. 휴지통 아이콘(영구 삭제)을 누르면 되돌릴 수 없어요.' },
    { q: '답변이 나오는 중에 대화를 지울 수 있나요?', a: '답변이 끝난 뒤에 지울 수 있어요.' },
    { q: '첨부 파일이 안 받아져요', a: '채팅 첨부는 30일이 지나면 «(만료)»로 바뀌고 더는 받을 수 없어요.' },
    { q: '한도가 넘었다고 떠요', a: '뜨는 창에 요청 사유를 적어 승인 요청을 보내면, 관리자가 승인한 뒤 계속 쓸 수 있어요.' },
  ], children: [
    { key: 'archive', title: '📥 보관함', lines: [
      '오래 두고 보고 싶은 대화는 사이드바 대화 옆 📥 버튼으로 "보관함"에 넣으세요.',
      '보관함에 넣은 대화는 50개 제한과 자동 삭제에서 빠져 영구 보관돼요.',
      '대화 목록 위 «보관함» 칩을 누르면 보관한 대화만 따로 모아 볼 수 있습니다.',
    ], steps: [
      { items: [
        '① 대화 줄의 📥 아이콘(보관함에 보관)',
        '② 목록 위 «보관함» 칩으로 모아 봅니다',
        '③ 되돌리려면 같은 자리 아이콘(보관 해제).',
      ] },
    ], tips: [
      '보관한 대화는 50개 수에 세지 않아요.',
    ] },
    { key: 'project', title: '📂 프로젝트', lines: [
      '관련 대화를 한 곳에 묶고, 공통 지침과 참고 문서를 넣어두는 공간이에요.',
      '넣어둔 문서는 그 프로젝트 안의 모든 대화가 자동으로 참고합니다.',
      '프로젝트를 선택하면 채팅 화면에 작업공간 파일 패널이 나타나요.',
      '작업공간을 켜면 AI가 작업공간 파일을 직접 읽고·고치고·목록을 봐서 여러 단계 작업을 이어서 처리해줍니다.',
      '작업공간을 켠 상태에서만 🐍 코드 실행(파이썬)이 가능해요 — 아래 "코드 실행" 항목을 보세요(별도 권한 필요).',
      '프로젝트 지식 문서는 답변 참고용이고, 작업공간 파일은 AI가 읽고 쓸 수 있는 파일입니다.',
      '👥 팀원과 공유할 수 있어요 — 프로젝트 설정에서 사람이나 그룹을 골라 보기/편집 권한을 줍니다.',
      '공유하면 상대가 그 프로젝트의 지침·지식문서·작업공간 파일을 함께 씁니다. 편집 권한이 있어야 파일을 고치거나 올릴 수 있어요.',
      '🔒 공유해도 서로의 대화는 안 보여요. 각자 자기 대화만 봅니다(같은 프로젝트에서 이어가도 마찬가지).',
      '예: "○○화주 운영" 프로젝트에 매뉴얼·지침을 넣고, 관련 질문을 그 안에서 이어가기.',
    ], steps: [
      { title: '만들기', items: [
        '① 왼쪽 «+ 새 프로젝트»',
        '② «프로젝트 이름» → «만들기»',
        '③ 프로젝트 줄의 ⚙ 톱니 아이콘(프로젝트 설정)에서 «프로젝트 지침»(역할·말투·규칙)과 «지식 문서»(이미지 5MB · 문서 100MB)를 넣고 «저장».',
      ] },
      { title: '팀원과 공유', items: [
        '① ⚙ 프로젝트 설정 안 «공유» 칸',
        '② «사용자» 또는 «그룹»을 고르고, 먼저 «읽기»/«편집» 중 하나를 고릅니다',
        '③ «이름 또는 이메일 검색»/«그룹 이름 검색» 칸에 입력한 뒤 검색 결과의 이름을 눌러 추가합니다',
        '④ 그만두려면 그 사람 옆 공유 해제 버튼.',
      ] },
    ], tips: [
      '공유받은 프로젝트는 목록에 «공유받음 · 역할»로 보여요.',
      '공유해도 서로의 대화는 안 보여요.',
    ] },
    { key: 'codeexec', title: '🐍 코드 실행 (작업공간 안에서)', lines: [
      '🔴 일반 채팅에는 없어요 — 프로젝트를 고른 뒤 "작업공간 ON" 을 켠 상태에서만 쓸 수 있고, 계정에 코드 실행 권한도 있어야 합니다.',
      '켜는 순서: ① 왼쪽에서 프로젝트 선택(또는 새 프로젝트) → ② 입력칸 바로 위 "작업공간 파일" 패널의 «작업공간 OFF» 버튼을 눌러 «작업공간 ON»으로 → ③ 계산·그래프를 말로 시키기.',
      '켜지면 AI가 파이썬을 직접 돌려서 결과를 파일로 만들어 줘요. 예: "이 엑셀 월별 합계 내서 막대그래프로 그려줘", "이 CSV 중복 행 정리해줘".',
      '만든 파일은 작업공간에 쌓이고, 다운로드해서 쓰면 됩니다.',
      '실행은 인터넷이 끊긴 격리 공간에서 돌아가요(외부로 나가는 코드는 실패합니다).',
      '작업공간 버튼이 안 보이면 프로젝트를 안 골랐거나 그 프로젝트에 편집 권한이 없는 거예요. 권한이 없으면 문의게시판으로 요청하세요.',
    ], steps: [
      { items: [
        '① 프로젝트를 고릅니다',
        '② 입력칸 바로 위 «작업공간 파일» 패널에서 «작업공간 OFF» 버튼을 눌러 «작업공간 ON»으로 바꿉니다',
        '③ «+ 파일 올리기»로 엑셀·CSV 를 올리고 말로 시킵니다',
        '④ 결과 파일을 눌러 내려받습니다.',
      ] },
    ], tips: [
      '파일 옆 «AI 읽기/쓰기 / AI 읽기 전용»으로 AI 가 고칠 수 있는지 보여요.',
      '«· 읽기 불가»가 붙은 파일은 AI 가 내용을 못 읽고 내려받기만 돼요.',
      '비어 있으면 «아직 파일이 없습니다…»가 보여요.',
    ] },
    { key: 'internal', title: '🏢 사내데이터(WMS) 조회', lines: [
      '채팅에서 🏢 사내데이터를 켜면 출고·재고·주문 같은 사내 데이터를 "말로" 물어볼 수 있어요(권한 필요).',
      '예: "다신샵 화주 6월 출고량 알려줘", "평택센터 어제 재고는?", "○○ 상품 보관재고 확인"',
      '운영 시스템 보호를 위해 조회는 읽기 전용이며, 기간은 1~2주 단위로 나눠 물으면 더 빨라요.',
      '권한이 없으면 🏢 버튼이 안 보여요. 필요하면 관리자에게 DB 조회 권한을 요청하세요(문의게시판).',
    ], steps: [
      { items: [
        '① «+» → «사내데이터» 켜기',
        '② «○○ 화주 6월 출고량»처럼 말로 묻습니다.',
      ] },
    ], faq: [
      { q: '«사내망 필요»가 떠요', a: '사내망에서만 쓸 수 있어요. 회사 밖이면 관리자에게 문의하세요.' },
      { q: '사내데이터 자리에 «요청하기»가 떠요', a: '사내데이터 권한이 없는 계정이에요. 문의게시판으로 요청하세요.' },
    ] },
    { key: 'docgen', title: '📄 문서 만들기', lines: [
      '"○○ 보고서/회의록/공문 써줘"라고 하면 서식이 잡힌 Word 문서로 만들어 다운로드 카드로 줘요.',
      '이어서 "표를 넣어줘", "앞에 요약을 붙여줘"처럼 다듬어 달라고 하면 됩니다.',
    ], steps: [
      { items: [
        '① «○○ 보고서 Word 로 써줘»처럼 요청',
        '② «문서 생성 중» 표시 뒤',
        '③ 답변 아래 나타나는 파일 카드를 누르면 내려받아요.',
      ] },
    ], tips: [
      '창을 닫아도 서버는 끝까지 만들어요 — 왼쪽 «만든 파일»에서 받으세요.',
    ] },
  ] },
  { key: 'generated', title: '📥 만든 파일', lines: [
    'AI가 만들어 준 문서(Word·엑셀·PPT 등)를 한곳에 모아 다시 받는 곳이에요.',
    '파일 만들기는 몇 분 걸리기도 해요. 그 사이 창을 닫거나 인터넷이 끊겨도 서버는 끝까지 만들어 두니, 여기서 받으면 됩니다.',
    '파일명으로 검색되고, 파일마다 형식·크기·만든 시각이 함께 보여요.',
  ], steps: [
    { items: [
      '① 왼쪽 메뉴 «만든 파일»',
      '② «파일명 검색» → «검색»',
      '③ 줄 오른쪽 «다운로드».',
    ] },
  ], faq: [
    { q: '«이 파일은 서버에 없습니다»가 떠요', a: '지워졌거나 보관 기간이 지난 파일이에요. 채팅에서 다시 만들어 달라고 하세요.' },
  ] },
  { key: 'docauto', title: '🧾 AI 문서 자동화', lines: [
    '거래명세서·송장·정산서를 올리면 AI가 읽어서 항목을 표로 뽑아줘요(스캔본·사진도 됩니다).',
    '뽑힌 값을 화면에서 확인·수정한 뒤 엑셀로 내보내면 돼요.',
    '담당자명·연락처 같은 개인정보는 서버에 저장할 때 자동으로 가려집니다.',
  ], steps: [
    { items: [
      '① 문서 종류를 고릅니다',
      '② 파일(이미지·PDF)을 고르고 «업로드 · 추출»',
      '③ «판독 신뢰도» 높음/보통/낮음을 보고 값 확인·수정',
      '④ «수정 저장» → «검수 완료»(쓸 수 없는 문서는 «반려»)',
      '⑤ «엑셀 내보내기».',
    ] },
  ], tips: [
    '«추출은 보조 도구이니 금액·수량은 반드시 원본과 대조하세요.»',
    '신뢰도가 낮음이면 특히 꼼꼼히 보세요.',
    '흐린 사진이면 «문서 추출에 실패했습니다. 이미지/PDF 화질을 확인하세요.»가 떠요.',
  ] },
  { key: 'meetings', title: '📝 AI 노트', lines: [
    '녹음 파일을 올리거나 브라우저에서 바로 녹음하면 → 자동 받아쓰기 · 화자(말한 사람) 구분 → AI가 안건·결정사항·할 일로 요약해줘요.',
    '회의뿐 아니라 미팅·면접 등 어떤 음성에도 쓸 수 있어요.',
    '요약 양식은 기본/폴더 양식/직접 입력 중 고르고, 다시 요약은 몇 번이든 가능합니다(재녹음 필요 없어요).',
    '받아쓴 내용·화자 이름을 고칠 수 있고, 시간을 누르면 그 지점부터 바로 들을 수 있어요.',
    '👥 공유는 폴더 단위예요 — 폴더 행의 공유 버튼에서 사람을 찾아 읽기 또는 편집 권한을 줍니다.',
    '공유하면 그 폴더 안의 회의록을 상대가 볼 수 있어요(편집 권한이면 전사·화자명·요약도 고칠 수 있습니다). 공유는 폴더 소유자만 관리해요.',
    '공유하고 싶지 않은 회의록은 그 폴더에 넣지 마세요 — 폴더에 넣는 순간 공유 대상이 됩니다.',
    '지운 노트는 휴지통에 보관되고, 보관 기간(기본 30일)이 지나면 자동으로 사라집니다.',
    '폴더 목록 맨 위 «홈»에서는 «최근 만든 노트»와 달력을 보고, «전체 노트 검색» 칸으로 모든 노트를 찾을 수 있어요.',
    'PC 에서 새 노트를 만들 때 «PC 소리도 함께 녹음»을 켜고 받는 방법을 고르면 화상회의 상대 목소리처럼 PC 에서 나는 소리도 같이 녹음돼요(휴대폰이나 지원하지 않는 브라우저에서는 이 항목이 보이지 않아요).',
  ], steps: [
    { title: '녹음해서 만들기', items: [
      '① 왼쪽 메뉴 «AI 노트» → «녹음»',
      '② 녹음 중 메모 칸(«Enter로 현재 시점에 저장»)과 «북마크»로 중요한 순간 표시',
      '③ 잠깐 멈추려면 일시정지 버튼(다시 누르면 재개)',
      '④ «종료» → 미리 듣기 뒤 «업로드». 그 자리에서 «다시 녹음»·«취소»도 고를 수 있어요.',
    ] },
    { title: '녹음 파일 올리기', items: [
      '① «새 노트»에서 제목·폴더·참석자를 정하고 파일을 첨부하거나 끌어다 놓습니다',
      '② «변환 대기 · 앞에 N건» → «전사 중 N%» → «화자 분석 중 (몇 분 소요될 수 있음)» 순서로 진행',
      '③ 끝나면 노트를 엽니다.',
    ] },
    { title: '요약 받기', items: [
      '① 노트의 «AI 요약»에서 «양식» — «기본 / 폴더 / 직접»',
      '② «요약 생성», 다시 하려면 «다시 요약»',
      '③ 예전 요약은 «버전 ▾» → «이 버전으로 복원». 직접 양식 예: «액션 아이템만 표로 정리 / 영어로 요약 / 3줄 요약».',
    ] },
    { title: '받아쓰기 고치기·듣기', items: [
      '① «편집»에서 문장을 그 자리에서 고치고, 문장 옆 ▶ 로 그 지점부터 들으며 확인 · «화자 이름 변경(전체 일괄)»',
      '② «완료»',
      '③ 문장 옆 시간을 누르면 그 지점부터 재생 · 플레이어의 되감기·빨리감기(5초) 버튼과 배속 버튼',
      '④ 머리의 «검색» 아이콘 → «노트 검색»(음성 기록·요약·메모·참석자) · 검색 줄 «단어 바꾸기».',
    ] },
    { title: '폴더 공유', items: [
      '① 왼쪽 «전체 노트» 줄의 «+»(폴더 추가) → 바로 아래 입력칸에 폴더 이름을 적고 Enter',
      '② 폴더 줄의 공유 아이콘 → «이름·이메일로 사용자 검색» → «읽기»/«편집»',
      '③ 폴더별 요약 양식은 폴더 목록 위 양식 설정 버튼.',
    ] },
  ], tips: [
    '«녹음 전 상대방의 동의를 구하는 에티켓을 지켜 주십시오.»',
    '녹음 중에는 새로고침·창 닫기를 하지 마세요 — 녹음이 브라우저 안에만 있어 사라질 수 있어요.',
    '긴 회의는 «전체 요약 / 앞부분만 (절약)»을 고를 수 있어요.',
    '노트를 «RAG 등록»하면 채팅 «문서 기반 답변»에서 찾을 수 있어요.',
  ], faq: [
    { q: '«오디오 보관 기간이 지났습니다»가 떠요', a: '원본 녹음은 보관 기간이 지나면 지워져요. 받아쓰기·요약은 남아요.' },
    { q: '«변환 실패»가 떠요', a: '«다시 변환»으로 다시 시도할 수 있어요.' },
    { q: '지운 노트는요?', a: '«휴지통»에서 «복원»을 누르세요. 보관 기간(기본 30일)이 지나면 자동으로 영구 삭제돼요. 실제 보관 일수는 휴지통 화면 위쪽 안내에 나와요.' },
  ] },
  { key: 'talk', title: '📹 화상회의', lines: [
    '사내 화상회의(영상통화)예요. 채널이나 1:1 대화에서 통화를 걸고, 화면 공유로 함께 회의하세요.',
    '회의를 녹화하면 자동으로 📝 AI 노트로 보내져 받아쓰기·요약(안건·결정사항·할 일)까지 이어집니다.',
    '브라우저에서 바로 참여할 수 있어요(별도 설치 불필요). ※ 드라이브와 함께 열리는 기능이에요(권한 필요).',
  ], steps: [
    { title: '바로 회의 시작하기', items: [
      '① 왼쪽 메뉴 «화상회의»',
      '② 처음 화면의 회의 시작 버튼을 누르고',
      '③ 대화 링크를 복사해 참석자에게 보냅니다(«링크 복사»).',
    ] },
    { title: '대화방 만들어 사람 부르기', items: [
      '① «새 대화 만들기» → 대화 이름을 적고 만듭니다(«대화 만들기»)',
      '② «참가자 추가»에서 동료를 고릅니다',
      '③ «통화 시작».',
    ] },
    { title: '통화 중 쓰는 버튼', items: [
      '아래 막대의 마이크·카메라 아이콘으로 켜고 끕니다.',
      '화면 공유 아이콘(«화면 공유») · «배경 흐리기» · «링크를 통해 손님이 참가하도록 허용»(대화 설정) · 손 아이콘(«손들기»).',
      '나갈 때 «통화 떠나기», 진행자는 «모든 이들에 대해 통화 끝내기».',
    ] },
    { title: '녹화하기', items: [
      '통화 중 메뉴에서 «녹음/녹화 시작» → 끝낼 때 «녹음/녹화 중단».',
    ] },
  ], tips: [
    '들어가기 전에 «장치 확인»으로 마이크·카메라를 먼저 시험해 보세요.',
    '마이크·카메라가 안 되면 브라우저 주소창 왼쪽 자물쇠에서 권한을 허용하세요(«마이크 접근이 거부되었습니다.»).',
    '음소거인 채 말하면 알려 줘요.',
    '녹화 파일은 자동으로 AI 노트로 넘어와 받아쓰기·요약까지 이어져요 — 몇 분 걸릴 수 있어요.',
  ] },
  { key: 'documents', title: '📚 AI 학습자료 등록', lines: [
    '사내 문서를 올리면 관리자 확인을 거쳐, AI가 더 정확히 답하도록 "지식 자료"로 쌓여요.',
    '같은 문서는 자동으로 걸러지고, 스캔본·이미지 속 글자도 AI가 읽어들입니다.',
  ], steps: [
    { items: [
      '① «클릭하거나 파일을 끌어다 놓으세요 (여러 개 가능)»',
      '② 비슷한 문서가 있으면 «유사 문서가 있습니다» 창에서 «다른 문서입니다 / 내 문서가 최신입니다 / 기존 문서가 최신입니다 (건너뛰기)» 중 선택',
      '③ «내가 올린 문서»에서 승인 대기 → 활성 상태 확인.',
    ] },
  ], tips: [
    '형식 «PDF · DOCX · XLSX · TXT · 파일당 최대 50MB»',
    'HWP·구형 DOC 는 PDF 로 저장해 올리기',
    '스캔본은 앞쪽 120쪽까지만 읽혀요',
    '암호 걸린 PDF·DRM 파일은 안 돼요',
    '개인정보·기밀은 가린 뒤 올리기',
    '좋은 자료 예: 업무 매뉴얼·규정·FAQ·양식·용어집·모범 예시',
  ], faq: [
    { q: '반려됐어요', a: '목록에 사유가 함께 보여요.' },
  ] },
  { key: 'messenger', title: '💬 메신저', lines: [
    '사내 메신저(Mattermost)예요. 채널과 1:1 대화(DM)로 소통하세요(권한 필요).',
    '@ouri(또는 "아우리")로 부르면 OURi가 대화 맥락을 보고 답해줘요(권한자는 1:1 DM에서 사내데이터 조회도 가능).',
    '누군가 나를 멘션하면 휴대폰 푸시 알림이 와요(아래 "모바일·알림" 참고). 사이드바 메뉴엔 새 메시지 빨간 점이 떠요.',
    '채널에 새로 들어와도 그 채널의 지난 대화를 모두 볼 수 있어요.',
  ], steps: [
    { title: '1:1 대화 시작하기', items: [
      '① 왼쪽 «개인 메시지» 제목 옆 ＋ 아이콘(«쪽지 쓰기»)을 누릅니다.',
      '② 이름을 입력해 고르고 시작합니다. 여러 명을 고르면 그룹 대화가 돼요.',
    ] },
    { title: '채널 찾아 들어가기', items: [
      '① 왼쪽 채널 목록 안의 «채널 추가» → «채널 보기»',
      '② 목록에서 채널을 골라 들어갑니다.',
    ] },
    { title: '답글(글타래) 달기', items: [
      '① 글에 마우스를 올리면 오른쪽 위에 아이콘들이 나타나요(휴대폰은 글을 길게 누름).',
      '② 말풍선 아이콘(«답글»)을 누르면 오른쪽에 글타래 창이 열려요.',
      '③ 내가 참여한 글타래는 왼쪽 «글타래»에서 모아 봅니다.',
    ] },
    { title: '파일·사진 보내기', items: [
      '입력칸의 클립 아이콘(«첨부»)을 누르거나 파일을 대화창에 끌어다 놓습니다.',
    ] },
    { title: '사람 부르기(멘션)', items: [
      '글에 @ 와 이름을 적으면 그 사람에게 알림이 가요. 나를 부른 글은 위쪽 @ 아이콘(«최근 멘션»)에서 모아 봅니다.',
    ] },
    { title: '중요한 글 모아 두기', items: [
      '① 글의 «···» 메뉴 → «중요 메시지로 지정» — 나만 보는 표시예요. 오른쪽 위 책갈피 아이콘에서 모아 봅니다.',
      '② 채널 모두에게 알릴 글은 «공지하기» — 채널 위쪽에서 볼 수 있어요.',
    ] },
    { title: '글 전달하기', items: [
      '글의 «···» 메뉴 → «전달하기» → 받을 채널이나 사람을 고릅니다.',
    ] },
    { title: '시끄러운 채널 조용히 하기', items: [
      '왼쪽 채널 이름에 마우스를 올려 «···» → «채널 알림 끄기». 나를 멘션한 글은 표시가 계속 떠요.',
    ] },
    { title: '잠시 알림 끄기', items: [
      '오른쪽 위 내 사진 → «방해 금지». 상태 문구는 «사용자 지정 상태 설정» — 예: «미팅 중»·«재택근무»·«휴가중».',
    ] },
    { title: '누가 읽었나 보기', items: [
      '내가 보낸 글 오른쪽 아래 «읽음 N»을 누르면 읽은 사람을 볼 수 있어요.',
    ] },
  ], tips: [
    '검색은 위쪽 검색칸(«검색»)에서 — 메신저 검색은 메신저 글만 찾아요.',
    '🔴 새 채널 만들기(«새로운 채널 생성»)는 관리자만 할 수 있어요 — 필요하면 문의게시판에 요청하세요.',
  ], faq: [
    { q: '메신저 로그아웃 버튼이 없어요', a: '게이트웨이에 로그인하면 메신저도 함께 열려서 따로 로그아웃할 필요가 없어요.' },
  ] },
  { key: 'drive', title: '📁 드라이브', lines: [
    '사내 파일 드라이브(Nextcloud)예요. 개인 파일과 공유 폴더를 쓸 수 있어요(권한 필요).',
    '✨ AI 버튼으로 문서 작성·요약을 돕고, Word·Excel·PPT는 브라우저에서 바로 열어 함께 편집합니다.',
    '드라이브 파일을 메신저로 보내 공유할 수도 있어요. 팀/부서 공유 폴더가 필요하면 관리자에게 요청하세요.',
  ], steps: [
    { title: '파일 올리기', items: [
      '① 올릴 폴더를 엽니다.',
      '② 파일을 목록 위에 끌어다 놓습니다(«이곳에 파일을 드래그 앤 드롭해 업로드»). 또는 «+ 새로 만들기»에서 업로드를 고릅니다.',
      '③ 🔴 다 올라갈 때까지 다른 폴더를 열지 마세요.',
    ] },
    { title: '새 폴더 만들기', items: [
      '«+ 새로 만들기» → «새 폴더» → «폴더 이름»을 적습니다.',
    ] },
    { title: '동료와 공유하기', items: [
      '① 파일·폴더 줄의 사람+ 아이콘을 누르면 오른쪽에 «공유» 창이 열려요.',
      '② «이름, 이메일, 또는 연합 클라우드 ID …» 칸에 동료 이름을 적고 고릅니다(부서 «그룹»도 가능).',
      '③ 권한을 고릅니다 — «읽기 전용» / «편집 허용» / «사용자 지정 권한».',
      '④ «공유 저장». 그만두려면 «공유 해제».',
    ] },
    { title: '링크로 공유하기', items: [
      '① 같은 «공유» 창에서 «공개 링크 만들기»',
      '② «암호 보호»와 «만료기한 설정»을 켜고 링크를 복사합니다. 폴더라면 «파일 업로드 전용»으로 남에게 파일을 받기만 할 수도 있어요.',
    ] },
    { title: '지운 파일 되살리기', items: [
      '왼쪽 아래 «삭제된 파일»을 열어 파일을 찾고 복원합니다.',
    ] },
    { title: 'AI 로 문서 도움받기', items: [
      '오른쪽 위 «AI» 버튼 → 요약·번역·글 쓰기 같은 작업을 고릅니다(이름은 관리자가 정한 AI 이름으로 보여요).',
    ] },
    { title: 'AI 채팅에 드라이브 파일 붙이기', items: [
      '채팅 «+» → «드라이브 첨부» → «드라이브에서 첨부» 창에서 «폴더 탐색 | 최근»으로 찾아 누르면 채팅에 붙어요(«파일을 누르면 채팅에 첨부됩니다.» · «문서 20MB · 이미지 5MB» · 문서·이미지·PDF·CSV/TXT 만).',
    ] },
  ], tips: [
    '링크는 받은 사람 누구나 로그인 없이 열 수 있어요 — 사내 동료와는 이름으로 공유하고, 링크는 꼭 암호·만료기한을 거세요.',
    'Word·Excel·PPT 는 누르면 브라우저에서 바로 열려 함께 편집돼요.',
  ], faq: [
    { q: '업로드가 실패해요', a: '올리는 동안 다른 폴더를 열었는지 확인하고 다시 올려 보세요. 큰 파일(수십 MB)이 검사 중에 끊기면 잠시 뒤 다시 올리세요.' },
  ] },
  { key: 'office', title: '🖥 오피스(문서 작성)', lines: [
    'Word·Excel·PowerPoint 문서를 브라우저에서 바로 만들고 편집해요(설치 불필요).',
    '"오피스" 메뉴에서 새 문서를 만들면 새 창에서 열리고, 작성한 파일은 드라이브에 저장됩니다.',
    '같은 문서를 팀원과 동시에 열어 함께 편집할 수도 있어요.',
  ], steps: [
    { items: [
      '① 왼쪽 «오피스»',
      '② «새 문서 / 새 스프레드시트 / 새 프레젠테이션» 중 하나',
      '③ 새 창 편집기에서 작성 — 자동 저장.',
    ] },
  ], tips: [
    '«새 문서는 드라이브의 "Office" 폴더에 저장됩니다.»',
    '만든 문서는 드라이브에 저장돼요. 나중에 다시 열 때는 왼쪽 메뉴 «드라이브»에서 엽니다.',
  ], faq: [
    { q: '«드라이브 연결에 실패했습니다»가 떠요', a: '잠시 뒤 «다시 시도»를 누르세요. 계속되면 문의게시판에 남겨 주세요.' },
  ] },
  { key: 'calendar', title: '📅 캘린더', lines: [
    '일정을 등록·관리하는 캘린더예요. 일/주/월 보기로 확인할 수 있어요.',
    '메인 화면 «새 메일 요약» 위젯에서 다우오피스 메일을 한 번 연결해 두면 회사 캘린더(다우오피스) 일정도 여기에 함께 보여요(읽기 전용 · 약 30분마다 갱신). 캘린더만 따로 연결하는 메뉴는 없어요. 연결했는데 안 보이면 문의게시판에 남겨 주세요.',
    '※ 드라이브와 함께 열리는 기능이에요(권한 필요).',
  ], steps: [
    { title: '내 캘린더를 동료에게 공유하기', items: [
      '① 왼쪽 메뉴 «캘린더»를 엽니다.',
      '② 왼쪽 캘린더 목록(«My calendars» 아래)에서 공유할 캘린더 이름에 마우스를 올리고 오른쪽 «⋯»를 누른 뒤 «달력 편집 및 공유»를 누릅니다.',
      '③ «사용자 및 그룹과 공유» 칸에 동료 이름을 입력하고, 목록에 뜬 사람(또는 부서 그룹)을 고릅니다.',
      '④ 기본은 보기만 공유예요. 상대도 일정을 넣고 고치게 하려면 그 사람 옆 편집 권한 체크(화면에는 영어로 «can edit and see confidential events»)를 켭니다. 켜면 가려 둔 일정 내용도 보여요.',
      '⑤ 끝 — 상대의 캘린더 목록에 내 캘린더가 자동으로 나타나요(상대가 따로 할 일 없음).',
    ] },
    { title: '공유 그만두기', items: [
      '«달력 편집 및 공유»에서 그 사람 옆 휴지통 아이콘(«(이름)과 공유 중단»)을 누릅니다.',
    ] },
    { title: '나와 공유된 캘린더 숨기기·빼기', items: [
      '목록에서 체크를 끄면 화면에서만 숨겨져요. 아예 빼려면 «⋯» → «나의 공유 해제».',
    ] },
    { title: '일정 하나만 가리기', items: [
      '일정을 열고 공개 범위에서 «전체 일정 공유» / «바쁨/한가함만 공유» / «일정 공유하지 않음» 중 고릅니다.',
      '«바쁨/한가함만 공유»를 고른 일정은 보기만 공유한 상대에게는 시간만 바쁨으로 보여요.',
      '🔴 편집 권한을 준 상대에게는 내용까지 보여요.',
      '«일정 공유하지 않음»을 고른 일정은 공유받은 사람에게 보이지 않아요.',
    ] },
  ], tips: [
    '공유는 캘린더 단위예요 — 팀과 나눌 일정만 모을 «새 달력»을 만들어 그것만 공유하면 개인 일정은 섞이지 않아요.',
    '이름 한두 글자만 쳐도 검색돼요.',
    '다우오피스에서 연결한 회사 캘린더(읽기 전용)는 본인만 보이고 여기서 공유할 수 없어요.',
    '«공개 링크 복사»·«삽입 코드 복사»로 만든 링크는 받은 사람 누구나 로그인 없이 볼 수 있으니 쓰지 마세요 — 동료와는 «사용자 및 그룹과 공유»로 나누세요.',
  ], faq: [
    { q: '다우오피스 «관심 캘린더»처럼 남의 캘린더를 신청해서 볼 수 있나요?', a: '아직은 안 돼요. 캘린더 주인이 위 방법으로 공유해 줘야 보여요. 필요하면 주인에게 부탁하세요.' },
    { q: '공유했는데 상대에게 안 보여요', a: '상대가 캘린더 화면을 새로고침했는지, 상대의 캘린더 목록에서 그 캘린더 체크가 켜져 있는지 확인하세요.' },
  ] },
  // 옛 '메일'(NC Mail)과 '메일2' 두 항목을 하나로 합쳤다(2026-08-24) — 화면이 하나뿐인데
  // 안내가 둘이면 사용자가 없는 메뉴를 찾는다. 키는 'mail2' 유지(권한 게이트가 그 키).
  { key: 'mail2', title: '✉️ 메일', lines: [
    '회사 메일(다우오피스)을 게이트웨이 안에서 읽고 보낼 수 있어요.',
    '메일 비밀번호를 한 번 연결해 두면(메인 화면의 "새 메일 요약" 위젯) 로그인 창 없이 바로 열립니다.',
    '메일을 열고 위쪽 📝 AI 요약 · 💬 AI 답장 초안 을 누르면 AI가 정리해 줘요(팝업으로 뜨고 복사할 수 있어요).',
  ], steps: [
    { title: '메일 쓰기', items: [
      '① «쓰기»',
      '② «받는 사람»·«참조»·«제목»을 적습니다',
      '③ 파일은 «파일 첨부»',
      '④ «보내기». 쓰다 멈추면 «임시 보관함»에 남아요.',
    ] },
    { title: '답장·전달', items: [
      '메일을 열고 «회신» · «전체 회신» · «전달».',
    ] },
    { title: 'AI 로 요약·답장 초안 받기', items: [
      '① 메일을 열고 위쪽 «AI 요약» 또는 «AI 답장 초안»을 누릅니다.',
      '② 결과는 창으로 뜨고, «복사»를 눌러 옮긴 뒤 «회신» 쓰기 창에 붙여 넣어 직접 고쳐 보냅니다(자동으로 보내지지 않아요).',
    ] },
    { title: '메일 정리하기', items: [
      '편지함 «받은 편지함»·«보낸 편지함»·«임시 보관함»·«지운 편지함»·«스팸 편지함»',
      '목록에서 «읽은 상태로 표시»/«읽지 않은 상태로 표시»',
      '«검색».',
    ] },
  ], tips: [
    'AI 초안은 꼭 읽고 고친 뒤 보내세요.',
  ] },
  { key: 'collabtools', title: '🧩 협업 도구', lines: [
    '«칸반»·«협업테이블»·«위키»·«설문»·«노트» 다섯 가지 도구로 팀 업무와 문서를 함께 정리하는 공간이에요. 도구를 누르면 새 창으로 열립니다.',
    '할 일을 카드로 옮기며 진행 상황을 관리하고, 위키에 팀 지식을 쌓아두세요.',
    '※ 드라이브와 함께 열리는 기능이에요(권한 필요).',
  ], steps: [
    { items: [
      '① «협업 도구»',
      '② «사용할 도구를 선택하세요. 새 창으로 열립니다.» — 카드를 누르면 새 창.',
    ] },
  ] },
  { key: 'centers', title: '🏭 센터 메뉴', lines: [
    '센터에서 쓰는 메뉴예요. 담당 센터가 지정된 분에게만 그 센터가 보입니다.',
  ], children: [
    { key: 'center-cost', title: '💰 센터 비용자료', lines: [
      '소모품비·인건비·운송료·보관비 자료를 일자별로 올려 쌓는 곳이에요.',
      '센터마다 양식이 달라서 지금은 원본 파일을 그대로 보관합니다(금액을 따로 입력하지 않아요).',
      '올릴 때 네 가지 중 어떤 비용인지 반드시 고르세요.',
    ], steps: [
      { items: [
        '① «일자»를 고르고 파일 선택(여러 개 가능)',
        '② 파일마다 «항목 선택 *»에서 «소모품비 / 인건비 / 운송료 / 보관비» 중 하나 — 고르지 않으면 «첨부마다 비용 항목을 선택해야 등록됩니다.»',
        '③ «메모(선택)»',
        '④ «등록». 소모품비 파일이면 «소모품비 명세 적재 완료»가 뜨고, 아래 «소모품비 명세»에서 «거래처별 / 품목별»로 모아 볼 수 있어요.',
      ] },
    ], tips: [
      '올린 원본은 «등록 원본»에서 «파일명·메모·작성자 검색»으로 찾아요.',
      '지우면 첨부 파일도 함께 지워져요.',
    ] },
    { key: 'center-all', title: '📊 센터 비용 통합조회', lines: [
      '각 센터가 올린 비용자료를 한 화면에서 모아 봐요(권한 있는 분만).',
      '기간을 정하면 날짜별·센터별로 자료가 올라왔는지 표로 보여 줘요. «미제출이 있는 날만»을 켜면 빠진 날만 남습니다.',
    ], steps: [
      { items: [
        '① «센터 비용 통합조회»',
        '② «기간»(기본 «최근 2주»)',
        '③ 칸의 숫자는 그날 올라온 건수, «—»는 미제출',
        '④ 표 머리의 센터를 누르면 그 센터 자료로 이동해요.',
      ] },
    ] },
    { key: 'center-daily', title: '📈 센터 일별 실적', lines: [
      '센터의 하루 실적(«일반출고»·«도착보장» 물량)을 고객사별로 입력하고, 보고서 형태로 확인하는 곳이에요.',
      '입력 탭에서 숫자를 넣고, 실적요약 탭에서 집계된 결과를 봅니다.',
    ], steps: [
      { items: [
        '① 센터·날짜 선택',
        '② «입력» 탭에서 «근무 · 도급» 값(«정규 인원 | 도급 인원 | 정규 시간(M.H) | 도급 시간(M.H) | 도급 금액»)과 «운영 특이사항» → «근무·도급 저장»',
        '③ «고객사 출고» 표를 확인·수정 → «출고 저장»',
        '④ «실적요약» 탭에서 결과 확인 → «엑셀 내려받기».',
      ] },
    ], tips: [
      '«자동 집계된 값을 고치면 수기로 바뀌어 이후 배치가 덮지 않습니다.»',
      '값이 이상하면 «자동 재집계»를 누르세요.',
      "고객사가 안 보이면 «'센터 고객사 명부'에서 먼저 등록하세요»가 떠요.",
    ] },
    { key: 'abc-analysis', title: '📊 ABC분석', lines: [
      '월별 SKU 출고량을 누적 비중 80%·95% 기준으로 A·B·C 등급화해요.',
      '연월 기본값은 서버에서 확인된 가장 최근 완료 월이며, 과거 완료 월도 선택해 조회할 수 있어요.',
      '매월 1~8일 08:10 KST에 전월 분석을 자동 시도하고, 완료된 달은 다시 만들지 않습니다.',
      '산정 대상은 그 달 말일 재고가 0보다 크거나 그 달에 수불이 있었던 상품이고, 출고가 0이면 C 등급으로 들어가요.',
    ], steps: [
      { items: [
        '① «연월»에서 볼 달을 고릅니다',
        '② 필요하면 «센터»·«화주»로 범위를 좁힙니다',
        '③ «조회»를 누릅니다',
        '④ 표 오른쪽 위 목록에서 «출고수량 많은 순»·«SKU코드 순»·«등급 순» 으로 순서를 바꿉니다',
        '⑤ 표 아래 «이전»·«다음»으로 페이지를 넘깁니다.',
      ] },
    ], tips: [
      '«↻ 재분석» 버튼은 권한이 있는 분에게만 보여요.',
    ] },
    { key: 'center-shippers', title: '🏢 센터 고객사 명부', lines: [
      '일별 실적의 뼈대예요 — 우리 센터에 어떤 고객사가 있고, 자동 집계인지 수기 입력인지 정리해 둡니다.',
      '여기에 등록된 고객사가 일별 실적 입력 화면에 나타나요.',
    ], steps: [
      { title: '하나씩 추가하기', items: [
        '① «+ 고객사 추가»',
        '② «표시명(보고서에 찍힘) / 법인명(사업자등록증) / 사업자번호», «시스템 / 출고구분 / 품온»',
        '③ 2.0 고객사는 «화주명·코드 검색»으로 화주코드 연결 — 저온/상온이 갈리면 «집계 창고»를 반드시 지정',
        '④ «저장».',
      ] },
      { title: '엑셀로 한꺼번에', items: [
        '① «업로드 양식» 내려받아 채우기',
        '② «엑셀 업로드»',
        '③ «엑셀 업로드 미리보기»에서 «신규만 추가» 또는 «기존도 덮어쓰기».',
      ] },
    ], tips: [
      '화주코드가 없으면 «미연결»로 보여요.',
      '더 안 쓰는 고객사는 지우지 말고 «사용 안 함» — 지우면 과거 보고서에서 사라져요.',
    ] },
  ] },
  { key: 'directory', title: '👥 조직도', lines: [
    '회사 조직도예요. 부서를 펼쳐 이름·부서·직책·이메일·연락처를 확인할 수 있어요.',
    '이름·아이디·부서·직위·전화번호로 검색돼요.',
    '정보는 다우오피스와 자동으로 동기화됩니다.',
  ], steps: [
    { items: [
      '① 검색칸(«이름/아이디/부서/직위/직책/전화»)에 입력',
      '② 부서를 펼쳐 이름을 누르면 «부서 / 직책 / 직위 / 이메일 / 연락처».',
    ] },
  ], tips: [
    '이름 옆 «(정지)»는 쓰지 않는 계정이에요.',
  ] },
  { key: 'dxboard', title: '📌 AX 보드', lines: [
    'AX 업무 보드예요. 주간회의·분석·일정 경과 탭으로 업무 진행을 봅니다.',
    '🔒 관리자가 명시적으로 허용한 사람만 볼 수 있어요(기본은 차단).',
  ] },
  { key: 'execmeeting', title: '🗂 임원회의', lines: [
    '임원회의 업무 화면이에요.',
    '🔒 관리자 또는 관리자가 명시적으로 허용한 사람만 볼 수 있어요.',
  ] },
  { key: 'supportboard', title: '🗂 경영지원팀 보드', lines: [
    '경영지원팀 업무 화면이에요.',
    '🔒 관리자 또는 관리자가 명시적으로 허용한 사람만 볼 수 있어요.',
  ] },
  { key: 'taskpm', title: '🗂 업무 프로젝트', lines: [
    '팀 업무를 프로젝트로 묶어 담당자·기간·상태로 진행 상황을 함께 보는 곳이에요.',
    '업무마다 설명·체크리스트·하위 업무·댓글·첨부파일·변경 기록을 한 화면에서 다룹니다.',
    '🔒 관리자가 명시적으로 허용한 사람만 볼 수 있어요(기본은 차단).',
  ], steps: [
    { title: '프로젝트 만들기', items: [
      '① 왼쪽 메뉴 «업무 프로젝트» → «새 프로젝트»',
      '② «이름»·«키»(영문 대문자로 시작하는 영숫자 2~10자)·«색»·«공개 범위»(«비공개»/«공개»)·«설명»을 정하고 «저장»',
      '③ 목록 위 «참여 중»·«공개» 탭으로 볼 프로젝트를 고릅니다.',
    ] },
    { title: '업무 등록', items: [
      '① 프로젝트를 열고 «새 업무»',
      '② «업무 제목», «담당자»(또는 «나에게 할당»), «시작일»·«마감일», «상태»·«유형»·«우선순위»를 정하고 «생성»',
      '③ 대시보드가 아닌 보기에서 «필터링»을 누르면 «전체»·«진행 중»·«완료»·«기한 지남»·«내 업무»와 제목 검색, 정렬(«번호»·«기한»·«최근 수정»)을 고를 수 있어요.',
      '④ 프로젝트 화면 위 탭 «대시보드»·«칸반»·«리스트»·«간트»·«캘린더»로 같은 업무를 다른 모양으로 봅니다(처음에는 «대시보드»가 열려요).',
      '⑤ 탭 줄 오른쪽 «노트»를 누르면 오른쪽에 노트 창이 열리고, «새 노트»로 회의 메모나 정리 글을 프로젝트에 남길 수 있어요.',
    ] },
    { title: '업무 상세', items: [
      '① 목록에서 업무 제목을 누르면 오른쪽에 업무 상세 창이 열려요.',
      '② «설명 편집»으로 설명을 쓰고, «체크리스트»·«하위 업무 추가»로 할 일을 나눕니다.',
      '③ «파일 첨부»로 파일을 올리고(끌어 놓아도 돼요) «댓글»로 의견을 남겨요. «기록» 탭에서 바뀐 내역을 봅니다.',
    ] },
    { title: '프로젝트 설정', items: [
      '① 프로젝트 화면 오른쪽 위 톱니 버튼',
      '② «정보»: 이름·설명·공개 범위를 고칩니다. 맨 아래 «위험 구역»에 «프로젝트 종료»가 있어요. 설정에는 «정보»·«멤버»·«항목»·«노트»·«자동화» 다섯 탭이 있습니다.',
      '③ «멤버»: «멤버 추가»에서 사람을 찾아 고르고 역할(«담당자»·«멤버»)을 정한 뒤 «추가»',
      '④ «항목»: 상태·유형·우선순위 목록을 «항목 추가»로 늘리거나 «위로»·«아래로»로 순서를 바꿉니다.',
      '⑤ «노트»: 노트를 고치고 지울 수 있는 사람을 «담당자와 멤버 모두» 또는 «노트를 만든 사람과 프로젝트 담당자만»으로 정합니다.',
      '⑥ «자동화»: «새 규칙»으로 조건에 따라 자동으로 처리할 규칙을 만들고, 규칙마다 켜고 끌 수 있어요(규칙 만들기·고치기는 PC 에서).',
      '설정은 프로젝트 «담당자» 역할만 바꿀 수 있어요.',
    ] },
  ], tips: [
    '업무 상세 맨 위 왼쪽의 업무 번호를 누르면 이 업무 주소가 복사돼요(«링크를 복사했습니다.»가 떠요). 그 프로젝트를 볼 수 있는 사람은 그 주소로 같은 업무를 바로 열 수 있어요.',
  ] },
  { key: 'contracts', title: '📑 계약 관리', lines: [
    '회사 계약서를 한곳에 모아 기준일(종료일·해지통보일)과 자동연장을 보고, 기한이 다가오면 메신저 채널로 알림을 받는 곳이에요.',
    '목록·90일 내·달력으로 다가오는 계약을 보고, 계약마다 원본 파일과 바뀐 기록이 남아요.',
    '🔒 관리자가 명시적으로 허용한 사람만 볼 수 있어요. 등록·수정·휴지통·내보내기·부서 관리는 편집 권한이 있는 사람만, 편집 권한·전체 이력·알림 설정·완전 삭제는 시스템 관리자만 할 수 있어요.',
  ], steps: [
    { title: '계약 찾기', items: [
      '① 왼쪽 메뉴 «계약 관리»',
      '② 위의 «목록»·«90일 내»·«달력» 중 보기를 고릅니다.',
      '③ 분류·부서를 고르거나 «계약번호·계약명·상대방»으로 찾고 «검색»',
      '«달력»에서는 ‹ › 로 달을 옮기고 «오늘»로 돌아오며, 위의 분류 체크 상자로 보일 분류를 고를 수 있어요.',
    ] },
    { title: '계약 등록(편집 권한)', items: [
      '① «계약 등록»',
      '② 원본 파일을 붙이고 분류·지역·당사자 구분·기간 유형·계약명·상대방·부서·담당자와 시작일·종료일을 채웁니다. «해지통보일»·«자동연장»도 정할 수 있어요.',
      '③ 계약번호는 비우면 자동으로 매겨져요. «저장»',
    ] },
    { title: '계약 보기·고치기', items: [
      '① 목록에서 계약을 누르면 상세가 열려요.',
      '② «정보»에서 내용을, «이력»에서 바뀐 기록을, «알림 기록»에서 이 계약으로 보낸 알림을 봅니다.',
      '③ 원본은 «내려받기», PDF 면 «열어 보기»로 바로 볼 수 있어요.',
      '④ 편집 권한이 있으면 «수정»으로 고치고, 필요 없으면 «휴지통으로 보내기» — «휴지통»에서 «복원»할 수 있어요.',
    ] },
    { title: '설정', items: [
      '① 목록 위 «설정»(편집 권한)',
      '② «부서»: 부서를 «추가»하거나 «이름 바꾸기»',
      '③ «편집 권한»: 사람을 골라 «편집 주기»·«편집 거두기» (시스템 관리자)',
      '④ «전체 이력»: 누가 무엇을 바꿨는지 모아 봅니다. (시스템 관리자)',
      '⑤ «알림»: «알림 채널»에 메신저 채널을 넣고 «글 항목»을 고르면 아래에 미리보기가 나와요. 확인하고 «저장» — 채널을 비우면 알림이 꺼져요. «마지막 실행»에서 지난 발송 결과를 봅니다. (시스템 관리자)',
    ] },
  ], tips: [
    '어제나 오늘 알림 발송이 실패하면 시스템 관리자의 계약 목록 위에 안내가 뜨고, «알림 설정 열기»로 바로 설정의 «알림» 탭으로 갈 수 있어요.',
    '«알림 채널»에 넣은 채널의 모든 사람이 알림을 봐요. 비공개 채널을 권하고, 비공개 채널이면 먼저 봇을 채널에 초대해 주세요.',
    '«내보내기»(편집 권한)는 지금 고른 부서·검색어로 엑셀을 받아요(목록·90일 내 보기에서는 분류도). 기간·달은 반영되지 않아요.',
  ] },
  { key: 'board', title: '📋 사내게시판', lines: [
    '글을 올리고 댓글로 소통하는 게시판이에요.',
    '중요한 안내는 각 화면 상단의 공지로도 표시됩니다. ※ 지금은 관리자·운영 관리자에게만 보여요(순차적으로 열리고 있어요).',
  ], steps: [
    { items: [
      '① «글쓰기»',
      '② 분류 «일반 / 정보공유 / 자유 / 건의», «제목»·«내용»',
      '③ «등록»',
      '④ 글을 열어 «댓글 입력» → «등록».',
    ] },
  ] },
  { key: 'inquiries', title: '📮 문의게시판', lines: [
    '관리자에게 문의·건의·한도 상향 요청 등을 남기는 곳이에요. 직접 작성·수정·삭제할 수 있어요.',
    '관리자가 답하면 상태가 «답변 완료»로 바뀌고 답변이 표시됩니다.',
  ], steps: [
    { items: [
      '① «새 문의»',
      '② «문의 유형» — «권한/계정 · 오류 신고 · 개선 요청 · 사용/문의 · 자료/문서 · 기타»',
      '③ «제목»·«문의 내용», 필요하면 «파일 첨부»',
      '④ «등록». 답이 오면 «답변 완료»로 바뀌고 앱 안 알림함에도 알림이 와요.',
    ] },
  ], tips: [
    '오류 신고는 일어난 시각과 화면 캡처를 함께 붙이면 빨라요.',
  ] },
  { key: 'devreq', title: '🛠 개발요청', lines: [
    '부서에서 필요한 개발을 회차마다 접수하고, AX 의 검토·진행 상황을 함께 보는 곳이에요.',
    '요청마다 «진행 여부»·«예정 완료»·«상태»가 보여요.',
    '내 요청의 «진행 여부»·예정일이 바뀌거나 개발 중·테스트·완료로 넘어가면 앱 안 알림함과 푸시로 알려 드려요.',
    '🔒 관리자가 명시적으로 허용한 사람만 볼 수 있어요(기본은 차단).',
  ], steps: [
    { title: '요청 등록', items: [
      '① 왼쪽 메뉴 «개발요청» → «+ 새 요청»',
      '② «부서»를 고르고 «제목»·«지금 하는 방식»·«원하는 기능»·«사용 대상·빈도»를 적어요. 필요하면 «참고자료»',
      '③ «공개 범위»는 관리자가 정해요 — «공개»는 다른 부서도 내용을 볼 수 있고, «비공개»는 요청 부서와 AX만 내용을 보고, 다른 부서에는 제목과 내용이 가려져요.',
      '④ «저장». 요청번호(예: 2-물류-07)가 붙어요.',
      '⑤ «지금 하는 방식»·«원하는 기능»·«참고자료» 칸 아래 «📎 파일 첨부»로 파일을 붙이거나, 캡처한 그림을 그 칸에 Ctrl+V 로 붙여 넣어요(끌어다 놓기도 돼요 · 파일당 100MB · 요청당 10개).',
    ] },
    { title: '부서 안 순위 정하기', items: [
      '① 목록 위 «부서 순위 정렬»',
      '② ↑·↓ 로 우리 부서 요청의 순서를 바꾸고 «저장»',
    ] },
    { title: '확인(테스트·완료)', items: [
      '① 상태가 «테스트»나 «완료»가 되면 요청을 열어 직접 써 봐요. (DEV 확인 계정을 받았으면 DEV 에서 써 봐요.)',
      '② «③ 진행·완료» 아래에서 «확인» 또는 «수정 필요»를 눌러요. «수정 필요»는 고칠 내용을 적고 «보내기»를 눌러요.',
    ] },
    { title: '추가 요청에 답하기', items: [
      '① 검토자가 추가로 요청하면 요청자에게 알림이 가요. (요청자 연결이 없는 요청은 알림이 없으니 목록에서 확인해 주세요.)',
      '② 그 요청을 열면 맨 위에 «검토자가 추가로 요청했어요»가 보여요.',
      '③ «답하기» → 글을 쓰거나 캡처를 붙이고 «답 보내기». 내용을 고쳐야 하면 «요청 내용 고치기».',
    ] },
  ], tips: [
    '접수가 열린 회차에서 «접수» 상태일 때, 그리고 검토자가 추가로 요청한 동안 내용을 고칠 수 있어요. 검토가 시작된 뒤 더할 말은 «메모 쓰기»로, 검토자가 요청한 것은 «답하기»로 보내 주세요.',
    '부서 순위는 접수 중인 회차에서, 부서 요청이 모두 «접수» 상태일 때만 바꿀 수 있어요.',
    '다른 부서의 요청은 고칠 수 없어요. 비슷한 요청은 AX 가 하나로 «통합»할 수 있어요.',
    '«현황판» 탭에서 회차별로 부서마다 접수 건수와 «진행»·«보류»·«반려»·«통합» 수, 예정 완료일이 지난 건을 볼 수 있어요.',
  ], faq: [
    { q: '«진행 여부»가 «보류»예요. 왜 그런가요?', a: '요청을 열면 AX 가 적은 사유가 보여요. 더 할 말이 있으면 «메모 쓰기»로 남겨 주세요.' },
    { q: '회차가 끝났는데 아직 안 된 요청은요?', a: '«반려»·«통합»이 아니고 끝나지 않은 요청은 다음 회차가 열릴 때 같은 요청번호로 넘어가고 «이월» 표시가 붙어요(넘어온 회차가 이월(1회차)처럼 적혀요). «통합»된 요청은 대표 요청이 넘어갈 때 함께 넘어가요. 부서 순위는 새 회차에서 다시 정해 주세요.' },
  ] },
  { key: 'tickets', title: '🎫 CS 티켓', lines: [
    '화주·센터 문의를 티켓으로 모아 관리하는 곳이에요.',
    '🔒 CS 이용 권한이 있는 계정만 볼 수 있어요.',
    '목록 화면 위 «티켓»·«문의 이력(AI 1차답변)» 탭으로 나뉘어요 — 티켓 목록에는 접수된 건만 올라오고, AI 답변으로 끝난 문의는 «문의 이력(AI 1차답변)» 탭에서 봅니다.',
  ] },
  { key: 'mobile', title: '📱 모바일 · 알림', lines: [
    '휴대폰에서도 PC와 같은 계정으로 AI 게이트웨이를 사용할 수 있어요. 홈 화면에 설치하면 주소창 없이 앱처럼 열리고 푸시 알림도 받을 수 있습니다.',
    '모바일 화면 설정과 푸시 구독은 브라우저·기기마다 따로 저장돼요. 아래 항목에서 설치, 화면 조절, 알림 종류와 문제 해결 방법을 확인하세요.',
  ], children: [
    { key: 'mobile-screen', title: '📱 모바일 화면 사용', lines: [
      '휴대폰이나 태블릿으로 접속하면 화면 폭과 터치 환경을 감지해 모바일 화면으로 자동 전환돼요.',
      '화면 아래에는 권한이 있는 주력 메뉴(메신저·드라이브·AI 채팅·AI 노트)와 "메뉴" 버튼이 고정됩니다. 키보드가 열릴 때는 입력칸을 가리지 않도록 하단 메뉴가 잠시 숨겨져요.',
      '하단의 "메뉴"를 누르면 전체 메뉴가 왼쪽에서 열립니다. 내 정보는 메뉴 목록에, 알림함은 메뉴 위쪽의 종 아이콘에, 화면 설정은 이 메뉴 맨 아래에 있어요. 로그아웃은 화면 오른쪽 위 내 계정 버튼에서 할 수 있어요.',
      '모바일 화면이 불편하거나 PC 화면이 필요하면 전체 메뉴 맨 아래 보기 줄에서 "PC"를 누르세요. 다시 "모바일"을 누르면 돌아옵니다.',
      '모바일 화면에서는 같은 줄의 −/+ 버튼으로 글자를 100%부터 250%까지 조절할 수 있어요. 화면 모드와 글자 크기는 현재 브라우저에 저장됩니다.',
      '다른 휴대폰이나 다른 브라우저에서는 화면 설정이 자동으로 따라가지 않으므로 그 기기에서 다시 설정하세요.',
    ] },
    { key: 'mobile-install', title: '📥 홈 화면에 설치', lines: [
      '설치 전 휴대폰 브라우저에서 https://ai.ourbox.co.kr 로 접속해 로그인하세요. 푸시 알림을 사용하려면 반드시 HTTPS 주소로 접속해야 합니다.',
      'Android: Chrome에서 오른쪽 위 메뉴(⋮)를 열고 "앱 설치" 또는 "홈 화면에 추가"를 선택한 뒤 설치를 확인하세요. 기종과 Chrome 버전에 따라 메뉴 이름이 다를 수 있어요.',
      'iPhone·iPad: Safari에서 아래쪽 공유 버튼을 누르고 "홈 화면에 추가"를 선택한 뒤 오른쪽 위 "추가"를 누르세요.',
      'iPhone·iPad의 푸시 알림은 Safari 탭이 아니라 홈 화면에 설치된 아이콘으로 서비스를 열었을 때만 켤 수 있어요.',
      '설치가 끝나면 홈 화면의 "AI 게이트웨이" 아이콘으로 실행하세요. 별도 회원가입 없이 기존 계정을 그대로 사용합니다.',
      '배포 후 화면이 이전 모습으로 남아 있으면 앱을 완전히 닫았다 다시 열거나 화면을 새로고침하세요. 그래도 같으면 브라우저 탭에서도 같은 주소를 열어 최신 화면인지 확인하세요.',
    ] },
    { key: 'notification-center', title: '🔔 앱 안 알림함', lines: [
      '앱 내부 알림함과 휴대폰 푸시 알림은 서로 다른 기능이에요. 앱 내부 알림함은 브라우저 알림 권한을 허용하지 않아도 사용할 수 있습니다.',
      'PC에서는 왼쪽 사이드바 맨 위의 종 모양 "알림", 모바일에서는 하단 "메뉴"를 연 뒤 아래쪽의 "알림"을 누르세요.',
      '읽지 않은 알림이 있으면 알림 버튼에 빨간 숫자 배지가 표시됩니다. 알림 한 건을 누르면 읽음 처리되고, 연결된 알림은 해당 화면으로 이동해요.',
      '"모두 읽음"을 누르면 현재 계정의 읽지 않은 앱 내부 알림이 한꺼번에 읽음 처리됩니다.',
      '일반 사용자에게는 한도 상향 승인·거절, 문의게시판 답변, CS 답변, AI 노트 폴더 공유 등이 앱 내부 알림으로 들어옵니다.',
      '관리자에게는 위 알림 외에 비용 예산, 서버 자원, 모델 변경, 기능 레지스트리, 사내데이터 연결 오류 같은 운영 알림도 들어올 수 있어요.',
      '메신저 메뉴의 빨간 점은 채널별 알림 설정을 반영한 미확인 메시지 수를 뜻하며, 알림함의 숫자 배지와는 별도로 집계됩니다. 화면을 다시 보거나 포커스하면 갱신되며 수십 초 정도 차이가 날 수 있어요.',
    ] },
    { key: 'push-setup', title: '🔔 푸시 알림 켜기 · 끄기', lines: [
      '푸시를 켜려면 "내 정보" → "알림 (모바일·PC 푸시)" → "알림 켜기"를 차례로 누르고, 브라우저나 운영체제가 묻는 알림 권한에서 "허용"을 선택하세요.',
      '설정 카드가 "알림 켜짐"으로 바뀌면 현재 브라우저의 구독이 완료된 상태예요.',
      '푸시 구독은 기기와 브라우저별로 따로 등록됩니다. PC에서 켰더라도 휴대폰에서 다시 켜야 하고, 같은 기기에서 푸시를 지원하는 브라우저를 두 개 쓰면 각각 설정해야 해요.',
      'iPhone·iPad에서는 홈 화면에 설치한 AI 게이트웨이 아이콘으로 연 뒤 "내 정보"에서 켜세요. Safari 탭에서만 열어 둔 상태로는 푸시를 켤 수 없어요.',
      '현재 구현에서 브라우저 푸시로 직접 전달되는 항목은 메신저 DM·멘션·채널 알림과, 사용자가 켜 둔 오늘의 모닝 브리핑이에요. 문의 답변·한도 처리·공유·운영 알림은 앱 내부 알림함에서 확인하세요.',
      '"알림 끄기"는 현재 브라우저의 구독을 해지하고 모닝 브리핑 푸시도 중지해요. 다른 기기에 남아 있는 메신저 푸시도 받지 않으려면 그 기기마다 각각 꺼 주세요.',
      '공용 PC나 다른 사람이 사용할 수 있는 기기에서는 푸시를 켜지 마세요. 메신저 메시지 일부가 운영체제 알림 미리보기에 표시될 수 있습니다.',
    ], faq: [
      { q: '«이 환경에선 푸시 알림을 사용할 수 없습니다»가 떠요', a: '브라우저가 푸시를 지원하지 않거나 인증서 문제예요. https://ai.ourbox.co.kr 로 접속했는지 확인하세요.' },
    ] },
    { key: 'messenger-push', title: '💬 메신저 알림 기준', lines: [
      '1:1 대화(DM)는 상대방에게 푸시가 전달됩니다. 일반 채널의 기본 설정은 "멘션만"이므로 @아이디로 직접 호출된 글이 푸시 대상이에요.',
      '채널 알림을 "전체"로 바꾸면 멘션이 없는 새 글도 받고, "끄기"로 바꾸면 그 채널에서는 멘션돼도 푸시를 받지 않아요. 채널별 설정이 사용자 전체 설정보다 우선합니다.',
      '그룹 메시지는 참여자 전체가 대상이고, 채널의 @channel·@all은 채널 멤버 전체, @here는 현재 온라인인 채널 멤버가 대상이에요.',
      '메신저 상태가 "방해 금지"이면 게이트웨이 푸시도 보내지 않습니다. 다시 받으려면 메신저 상태를 온라인 또는 자리 비움 등으로 바꾸세요.',
      '메신저 메뉴의 빨간 점은 채널별 알림 설정에 따른 미확인 수이고, 홈의 "메신저 알림" 위젯은 나를 호출한 미확인 멘션 수예요. 두 숫자는 집계 범위가 달라 서로 다를 수 있으며, 푸시를 놓쳤다면 각 표시를 눌러 메신저에서 직접 확인하세요.',
      'OURi 봇의 일반 글은 채널 알림을 "전체"로 켠 사용자에게만 푸시됩니다. 기본 "멘션만" 상태에서는 OURi가 쓴 일반 글만으로 푸시가 울리지 않아요.',
    ] },
    { key: 'notification-troubleshooting', title: '⚙️ 알림 문제 해결', lines: [
      '먼저 "내 정보"의 알림 카드가 "알림 켜짐"인지 확인하세요. "알림 꺼짐"이면 현재 기기에서 다시 켜야 합니다.',
      '"알림 권한이 거부되었습니다"가 나오면 브라우저의 사이트 설정에서 ai.ourbox.co.kr 알림을 "허용"으로 바꾸고 페이지를 새로고침한 뒤 다시 켜세요.',
      '브라우저에서는 켜졌지만 알림이 보이지 않으면 휴대폰 또는 PC의 운영체제 설정에서 해당 브라우저나 "AI 게이트웨이"의 알림이 허용돼 있는지 확인하세요. 잠금 화면·배너·소리 표시는 운영체제 설정을 따릅니다.',
      'iPhone·iPad에서 켜기 버튼이 동작하지 않으면 Safari의 공유 메뉴로 홈 화면에 설치했는지, 설치된 아이콘으로 실행했는지 확인하세요.',
      '메신저 알림만 오지 않으면 해당 채널 설정이 "끄기"인지, 내 메신저 상태가 "방해 금지"인지, 글이 DM·내 멘션·전체 알림 대상인지 확인하세요.',
      '앱 내부 알림함은 보이는데 푸시만 오지 않는 경우에는 푸시 구독 또는 기기 권한 문제예요. 두 기능은 별도이므로 알림함 표시만으로 푸시 설정 완료를 뜻하지 않습니다.',
      '위 항목을 확인해도 해결되지 않으면 문의게시판에 기기 종류, 운영체제, 브라우저 이름, "알림 켜짐/꺼짐" 상태, 발생 시각, 받지 못한 알림 종류를 함께 적어 주세요.',
    ] },
  ] },
  { key: 'general', title: '⚙️ 계정 · 이용 전반', lines: [
    '특정 메뉴가 아니라 서비스 전반에 해당하는 안내예요. 아래 항목을 확인하세요.',
  ], children: [
    { key: 'limit', title: '📊 사용량 / 한도', lines: [
      '오른쪽 위 동그란 내 계정 단추를 누르면 나오는 창에서 이번 달 토큰·웹검색 사용량을 볼 수 있어요(그룹 공유 한도면 그룹 합산으로 표시).',
      '한도를 늘리고 싶으면 채팅의 한도 요청 또는 문의게시판으로 신청하세요.',
    ], tips: [
      '«내 정보»에서 «이번 달 토큰 사용량»·«이번 달 웹 검색 사용량»과 한도 %를 볼 수 있어요.',
    ] },
    { key: 'security', title: '🔒 보안 · 로그인 · 내 정보', lines: [
      '회사 정책에 따라 로그인 시 2단계 인증(2FA)이 적용될 수 있어요.',
      '회사메일(@ourbox.co.kr)은 보통 이메일로 받은 6자리 코드, 그 외 계정은 인증 앱(Google OTP 등)을 씁니다.',
      '비밀번호를 잊었으면 로그인 화면의 «비밀번호를 잊으셨나요?»를 눌러 «재설정 링크 전송»을 이용하세요. 5회 틀리면 계정이 잠깁니다.',
      '왼쪽 사이드바 메뉴의 «내 정보»에서 알림 켜기, 화면 폰트, 비밀번호 변경 등 개인 설정을 관리할 수 있어요.',
    ], steps: [
      { title: '비밀번호 바꾸기', items: [
        '① «내 정보» → «비밀번호 변경»',
        '② «현재 비밀번호»·«새 비밀번호»(8자 이상)·«새 비밀번호 확인»',
        '③ «비밀번호 변경».',
      ] },
      { title: '이름 바꾸기', items: [
        '«기본 정보»의 이름 옆 «수정» → «저장».',
      ] },
      { title: '처음 가입하기', items: [
        '① 로그인 화면에서 회원가입',
        '② 받은 인증 메일의 링크를 눌러 이메일 인증',
        '③ 관리자가 승인하면 알림 메일이 와요',
        '④ 그 뒤 로그인.',
      ] },
    ] },
    { key: 'retention', title: '🗄 내 데이터 보관', lines: [
      '대화는 직접 지우기 전까지 보관돼요. 다만 보관 중인 대화가 50개가 된 상태에서 새 대화를 시작하면, 확인을 거쳐 오래된 것부터 지워집니다(보관함에 넣은 대화는 세지 않아요).',
      '채팅에 올린 첨부 파일은 30일이 지나면 서버에서 지워져 더는 받을 수 없어요.',
      '계정이 삭제되면 첨부 파일은 함께 지워지고, 대화 본문은 3년간 보존됩니다 — 인수인계·사고 조사 목적이며 시스템 관리자만 열람할 수 있고 열람 기록이 남아요. 3년이 지나면 자동으로 파기됩니다.',
      '사용량·비용 기록은 조직 장부라 계정 삭제 후에도 남아요(대화 내용은 들어 있지 않습니다).',
      '같은 내용을 "내 정보" 화면에서도 볼 수 있어요.',
    ] },
  ] },
];

// 캡처 배치 표 — 노드 key → { shots(PC 캡처), phoneShots(휴대폰 캡처) }.
// 🔴 본문(HELP_DEFS)과 분리해 둔다: 배치가 한 표에 모여야 전수 대조가 의미를 갖는다.
//    이름은 utils/helpShots.js 의 SHOTS 키와 같아야 한다.
const SHOTS_BY_KEY = {
  start: { shots: ['d_home', 'd_help'], phoneShots: ['m_home', 'm_home_drawer'] },
  chat: { shots: ['d_chat', 'd_chat_conv'], phoneShots: ['m_chat'] },
  archive: { shots: ['d_archive'] },
  project: { shots: ['d_project_panel'] },
  codeexec: { shots: ['d_workspace_on'] },
  internal: { shots: ['d_internal_data_on'] },
  docgen: { shots: ['d_chat_docgen'], phoneShots: ['m_chat_docgen'] },
  generated: { shots: ['d_generated'] },
  docauto: { shots: ['d_doc_automation'] },
  meetings: { shots: ['d_meetings', 'd_meetings_detail'], phoneShots: ['m_meetings'] },
  talk: { shots: ['d_talk'] },
  documents: { shots: ['d_documents'] },
  messenger: { shots: ['d_messenger'], phoneShots: ['m_messenger'] },
  drive: { shots: ['d_drive'], phoneShots: ['m_drive'] },
  office: { shots: ['d_office'] },
  calendar: { shots: ['d_calendar'] },
  collabtools: { shots: ['d_collab_tools'] },
  'center-all': { shots: ['d_center_costs'] },
  'center-daily': { shots: ['d_center_daily'] },
  'abc-analysis': { shots: ['d_abc_analysis'] },
  'center-shippers': { shots: ['d_center_shippers'] },
  directory: { shots: ['d_directory'] },
  dxboard: { shots: ['d_dx_board'] },
  execmeeting: { shots: ['d_exec_meeting'] },
  supportboard: { shots: ['d_support_board'] },
  contracts: { shots: ['d_contracts'] },
  board: { shots: ['d_board'] },
  inquiries: { shots: ['d_inquiries', 'd_inquiries_detail'] },
  tickets: { shots: ['d_tickets'] },
  'mobile-screen': { phoneShots: ['m_mobile_screen_settings'] },
  'notification-center': { phoneShots: ['m_mobile_notification_center'] },
  // 🔴 «알림 켜기»(push-setup)에는 d_my 를 쓰지 않는다 — 그 캡처에 알림 칸이 안 보인다.
  'push-setup': { phoneShots: ['m_mobile_push_card'] },
  limit: { shots: ['d_my'] },
  security: { shots: ['d_auth_login', 'd_auth_register', 'd_auth_forgot', 'd_my_settings'], phoneShots: ['m_auth_login', 'm_auth_register', 'm_auth_forgot'] },
};

// 캡처 배치를 붙인 최종 도움말 트리.
const withShots = (node) => ({
  ...node,
  ...(SHOTS_BY_KEY[node.key] || {}),
  ...(node.children ? { children: node.children.map(withShots) } : {}),
});
const HELP = HELP_DEFS.map(withShots);
// 전수 대조 시험(HelpPage.test.jsx)이 배치를 읽는다 — 화면은 기본 export 를 쓴다.
export { HELP };

const LINE_TEXT = { fontSize: '14px', color: COLORS.text, lineHeight: 1.6, wordBreak: 'keep-all', overflowWrap: 'anywhere' };
const INTRO_TEXT = { fontSize: '15px', color: COLORS.text, lineHeight: 1.7, margin: '0 0 14px', wordBreak: 'keep-all', overflowWrap: 'anywhere' };
const CALLOUT_BOX = { backgroundColor: TONES.yellow.soft, padding: '8px 12px', borderRadius: RADIUS.md };
const CHIP = { display: 'inline-flex', alignItems: 'center', padding: '6px 11px', border: `1px solid ${COLORS.primaryBorder}`, borderRadius: '999px', background: COLORS.primarySoft, color: COLORS.primaryDark, fontSize: '12.5px', fontWeight: 500, cursor: 'pointer' };
const SECTION_LABEL = { fontSize: '13px', fontWeight: 620, color: COLORS.text, margin: '0 0 8px' };
const STEP_GROUP_TITLE = { fontSize: '13.5px', fontWeight: 600, color: COLORS.text, margin: '0 0 6px' };
const STEP_MARK = { flexShrink: 0, width: '20px', height: '20px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11.5px', fontWeight: 700, lineHeight: 1 };
const STEP_BADGE = { ...STEP_MARK, background: COLORS.primarySoft, border: `1px solid ${COLORS.primaryBorder}`, color: COLORS.primaryDark };
const STEP_DOT = { ...STEP_MARK, color: COLORS.textMuted, fontWeight: 400 };
const TIP_BOX = { backgroundColor: COLORS.primarySoft, borderRadius: RADIUS.md, padding: '10px 12px' };
const FAQ_ITEM = { border: `1px solid ${COLORS.borderSoft}`, borderRadius: '8px', padding: '8px 12px', background: COLORS.surface };
// 🔒·🔴 로 시작하는 줄만 강조 상자로 그린다(글·순서는 그대로).
const isCallout = (line) => /^(🔒|🔴)/.test(String(line || '').trim());

// 절차 글이 «① …» 로 시작하면 그 번호를 배지로 옮긴다 — 안 그러면 번호가 두 번 보인다.
// 번호가 없는 줄(보충 설명 등)은 배지 대신 점을 찍어 순서가 있는 척하지 않는다.
const CIRCLED_NO = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫';
const splitStep = (item) => {
  const text = String(item ?? '');
  const at = CIRCLED_NO.indexOf(text.charAt(0));
  return at >= 0 ? { no: String(at + 1), text: text.slice(1).trim() } : { no: null, text };
};

// 검색은 캡처 캡션도 대상으로 한다("보관함 화면" 처럼 화면 이름으로 찾는 사람이 있다).
const shotCaptions = (node) => [...(node.shots || []), ...(node.phoneShots || [])]
  .map((name) => (SHOTS[name] ? SHOTS[name].caption : ''))
  .filter(Boolean);

// 검색은 절차·팁·질문 글도 대상으로 한다("프롬프트 라이브러리" 처럼 본문 개요에는 없고
// 절차에만 나오는 말로 찾는 사람이 있다).
const stepTexts = (node) => (node.steps || []).flatMap((g) => [g.title || '', ...(g.items || [])]);
const faqTexts = (node) => (node.faq || []).flatMap((f) => [f.q, f.a]);

const matchesNode = (node, q) => {
  const haystack = [
    node.title, ...(node.lines || []), ...shotCaptions(node),
    ...stepTexts(node), ...(node.tips || []), ...faqTexts(node),
  ].join(' ').toLowerCase();
  return haystack.includes(q);
};

const filterHelp = (query) => {
  const q = query.trim().toLowerCase();
  if (!q) return HELP;
  return HELP.map((top) => {
    const children = (top.children || []).filter((c) => matchesNode(c, q));
    if (matchesNode(top, q)) return top;
    if (children.length) return { ...top, children };
    return null;
  }).filter(Boolean);
};

// 검색어가 바뀔 때 지금 선택을 그대로 둘지 판단한다.
// 🔴 "그 키가 결과 트리에 남아 있나"가 아니라 "그 항목 자신이 걸렸나"를 본다 — 부모를 먼저
//    고른 상태에서 자식만 걸리는 검색어(예: «AI 채팅» 을 고른 뒤 "보관함 화면")를 넣으면
//    부모는 자식을 담는 껍데기로만 남으므로, 적중한 자식으로 선택이 옮겨가야 한다.
//    부모 섹션이 통째로 걸렸을 때는 자식 선택을 그대로 둔다.
// 검색어를 지우면 q='' 라 항상 걸려(includes('')) 선택이 튀지 않는다.
const keepsSelection = (items, key, query) => {
  const q = query.trim().toLowerCase();
  const hit = (node) => matchesNode(node, q);
  return items.some((top) => {
    if (top.key === key) return hit(top);
    return (top.children || []).some((c) => c.key === key && (hit(c) || hit(top)));
  });
};

const HELP_ICONS = {
  start: IconSparkles,
  chat: IconMessageCircle,
  archive: IconArchive,
  project: IconFolder,
  internal: IconBuilding,
  docgen: IconFileText,
  meetings: IconNotebook,
  talk: IconVideo,
  documents: IconBook2,
  messenger: IconMessageCircle,
  drive: IconFolder,
  office: IconFileText,
  calendar: IconCalendar,
  mail: IconMail,
  collabtools: IconPuzzle,
  directory: IconUsers,
  dxboard: IconLayoutKanban,
  execmeeting: IconPresentation,
  supportboard: IconLayoutKanban,
  taskpm: IconLayoutKanban,
  contracts: IconFileInvoice,
  board: IconClipboardList,
  inquiries: IconHelpCircle,
  devreq: IconClipboardList,
  tickets: IconTicket,
  generated: IconDownload,
  docauto: IconFileInvoice,
  mail2: IconMail,
  centers: IconBuildingWarehouse,
  'center-cost': IconCoin,
  'center-all': IconChartBar,
  'center-daily': IconTrendingUp,
  'abc-analysis': IconChartBar,
  'center-shippers': IconBuilding,
  codeexec: IconCode,
  retention: IconDatabase,
  general: IconSettings,
  limit: IconChartBar,
  security: IconLock,
  mobile: IconPhone,
  'mobile-screen': IconPhone,
  'mobile-install': IconDownload,
  'notification-center': IconBell,
  'push-setup': IconBell,
  'messenger-push': IconMessageCircle,
  'notification-troubleshooting': IconSettings,
};

const cleanTitle = (title) => title.replace(/^[^A-Za-z0-9가-힣]+\s*/, '');

function HelpTitle({ node, size = 15 }) {
  const Icon = HELP_ICONS[node.key] || IconHelpCircle;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
      <Icon size={size} stroke={1.8} aria-hidden="true" style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cleanTitle(node.title)}</span>
    </span>
  );
}

const firstVisibleKey = (items, query) => {
  if (!items.length) return null;
  const q = query.trim().toLowerCase();
  if (q && !matchesNode(items[0], q) && (items[0].children || []).length) return items[0].children[0].key;
  return items[0].key;
};

export default function HelpPage() {
  const mobile = useIsMobile();   // 모바일: nav 전체폭으로 위에 쌓이고 본문 전체폭
  const [sel, setSel] = useState('start');
  const [query, setQuery] = useState('');
  const findNode = (key) => {
    for (const top of HELP) {
      if (top.key === key) return top;
      for (const c of (top.children || [])) if (c.key === key) return c;
    }
    return HELP[0];
  };
  const visibleHelp = filterHelp(query);
  const handleQuery = (e) => {
    const next = e.target.value;
    const nextItems = filterHelp(next);
    setQuery(next);
    if (nextItems.length && !keepsSelection(nextItems, sel, next)) setSel(firstVisibleKey(nextItems, next));
  };
  const cur = findNode(sel);
  return (
    <AppShell app="help">
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        <Page>
          <PageHeader icon={IconBook} tone="purple" title="사용 안내" subtitle="각 기능의 사용법을 확인하세요. 메뉴 옆 ▸를 누르면 그 메뉴 안의 기능이 펼쳐져요." />
          <Announcements scope="help" />
          <input value={query} onChange={handleQuery} placeholder="도움말 검색" style={{ ...inputBase, maxWidth: mobile ? undefined : '360px', marginBottom: '16px' }} />
          <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* 데스크톱 목차는 스크롤 상자 안에서 sticky — 머리·공지 배너 높이와 무관하게 «보이는 높이 − 위아래 16» 까지만 */}
            <div style={{ ...cardBase, padding: '4px 0', width: mobile ? '100%' : '230px', flexShrink: 0, overflow: 'hidden', ...(mobile ? {} : { position: 'sticky', top: '16px', alignSelf: 'flex-start', maxHeight: 'calc(var(--gw-app-h, 100dvh) - 32px)' }), overflowY: 'auto' }}>
              {visibleHelp.length === 0 && (
                <div style={{ padding: '18px 16px', fontSize: '13px', color: COLORS.textMuted, textAlign: 'center' }}>검색 결과가 없습니다.</div>
              )}
              {visibleHelp.map((h) => {
                const hasKids = (h.children || []).length > 0;
                const open = sel === h.key || (h.children || []).some((c) => c.key === sel);
                const active = sel === h.key;
                return (
                  <div key={h.key}>
                    <button onClick={() => setSel(h.key)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%', textAlign: 'left', padding: '11px 16px', border: 'none', borderLeft: `3px solid ${active ? COLORS.primary : 'transparent'}`, cursor: 'pointer', fontSize: '14px', fontWeight: active ? 700 : 500, background: active ? COLORS.primarySoft : COLORS.surface, color: active ? COLORS.selectedText : COLORS.text }}>
                      <HelpTitle node={h} size={16} />
                      {hasKids && <span style={{ fontSize: '11px', color: COLORS.textMuted }}>{open ? '▾' : '▸'}</span>}
                    </button>
                    {open && (h.children || []).map((c) => {
                      const ca = sel === c.key;
                      return (
                        <button key={c.key} onClick={() => setSel(c.key)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 16px 9px 30px', border: 'none', borderLeft: `3px solid ${ca ? COLORS.primary : 'transparent'}`, cursor: 'pointer', fontSize: '13px', fontWeight: ca ? 700 : 500, background: ca ? COLORS.primarySoft : COLORS.surface, color: ca ? COLORS.selectedText : COLORS.textSub }}>
                          <HelpTitle node={c} size={15} />
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div style={{ ...cardBase, flex: 1, minWidth: mobile ? 0 : '320px', width: mobile ? '100%' : undefined, padding: mobile ? '16px' : '18px 22px' }}>
              <h2 style={{ fontSize: '17px', fontWeight: 620, color: COLORS.text, marginBottom: '14px' }}><HelpTitle node={cur} size={18} /></h2>
              {/* 첫 줄은 글머리표 없는 도입 문단 — 카드를 열면 "무엇을 하는 곳인지"가 먼저 읽히게. */}
              <p data-callout={isCallout(cur.lines[0]) ? 'true' : undefined}
                style={{ ...INTRO_TEXT, ...(isCallout(cur.lines[0]) ? CALLOUT_BOX : null) }}>{cur.lines[0]}</p>
              {cur.lines.length > 1 && (
                <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {cur.lines.slice(1).map((ln, i) => (
                    <li key={i} data-callout={isCallout(ln) ? 'true' : undefined}
                      style={isCallout(ln) ? { ...LINE_TEXT, ...CALLOUT_BOX, listStyle: 'none', marginLeft: '-18px' } : LINE_TEXT}>{ln}</li>
                  ))}
                </ul>
              )}
              {(cur.steps || []).length > 0 && (
                <div data-testid="help-steps" style={{ marginTop: '18px' }}>
                  <div style={SECTION_LABEL}>이렇게 해 보세요</div>
                  {cur.steps.map((group, gi) => (
                    <div key={gi} style={{ marginTop: gi ? '12px' : 0 }}>
                      {group.title && <div style={STEP_GROUP_TITLE}>{group.title}</div>}
                      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(group.items || []).map((item, i) => {
                          const step = splitStep(item);
                          return (
                            <li key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                              <span aria-hidden="true" style={step.no ? STEP_BADGE : STEP_DOT}>{step.no || '·'}</span>
                              <span style={LINE_TEXT}>{step.text}</span>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  ))}
                </div>
              )}
              {(cur.tips || []).length > 0 && (
                <div data-testid="help-tips" style={{ marginTop: '18px', ...TIP_BOX }}>
                  <div style={{ ...SECTION_LABEL, margin: '0 0 6px', color: COLORS.primaryDark }}>💡 알아 두면 좋아요</div>
                  <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {cur.tips.map((tip, i) => <li key={i} style={LINE_TEXT}>{tip}</li>)}
                  </ul>
                </div>
              )}
              {(cur.faq || []).length > 0 && (
                <div data-testid="help-faq" style={{ marginTop: '18px' }}>
                  <div style={SECTION_LABEL}>자주 묻는 질문</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {cur.faq.map((item, i) => (
                      <details key={i} style={FAQ_ITEM}>
                        <summary style={{ cursor: 'pointer', fontSize: '13.5px', fontWeight: 600, color: COLORS.text }}>{item.q}</summary>
                        <div style={{ ...LINE_TEXT, marginTop: '8px' }}>{item.a}</div>
                      </details>
                    ))}
                  </div>
                </div>
              )}
              {(cur.shots || cur.phoneShots) && (
                <div style={{ marginTop: '18px' }}>
                  <HelpShotGallery key={cur.key} names={cur.shots || []} phoneNames={cur.phoneShots || []} mobile={mobile} />
                </div>
              )}
              {(cur.children || []).length > 0 && (
                <div style={{ marginTop: '18px', borderTop: `1px solid ${COLORS.borderSoft}`, paddingTop: '14px' }}>
                  <div style={{ fontSize: '12.5px', color: COLORS.textSub, marginBottom: '8px' }}>이 메뉴 안의 기능</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {cur.children.map((c) => (
                      <button key={c.key} type="button" onClick={() => setSel(c.key)}
                        aria-label={`${cleanTitle(c.title)} 안내 보기`} style={CHIP}>
                        <HelpTitle node={c} size={14} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Page>
      </div>
    </AppShell>
  );
}
